"use client";

/**
 * Section accordéon de la navigation mobile du header (< lg) : un bouton de
 * bascule (disclosure) qui plie/déplie la liste des liens du pilier
 * (Chauffeur / Location / Partenaires).
 *
 * Extrait tel quel du `Header` pour supprimer la triplication : le markup et
 * les classes Tailwind sont identiques à l'original. L'état ouvert/fermé reste
 * géré par le parent (`open` / `onToggle`). Les `href` restent canoniques FR et
 * sont localisés au rendu via `localeNavHref`.
 */
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import { type Locale } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { type NavServiceItem } from "@/components/layout/navDict";

type Props = {
  /**
   * Libellé déjà résolu dans la locale courante (ex. `t.triggerChauffeur[locale]`).
   * `string | undefined` car l'accès `I18nText[locale]` est optionnel pour `ar`
   * (déploiement incrémental) ; on transmet la valeur telle quelle, à l'identique
   * de l'usage inline d'origine (React ignore un enfant `undefined`).
   */
  label: string | undefined;
  /** Services du pilier (FR `href` canoniques + libellés i18n). */
  items: NavServiceItem[];
  /** Section dépliée ou non (état porté par le parent). */
  open: boolean;
  /** Bascule l'état déplié de la section. */
  onToggle: () => void;
  /** Locale détectée du chemin courant (pour localiser les `href`/libellés). */
  locale: Locale;
  /** Contexte « location » → accents or au lieu du primaire. */
  isLocationRoute: boolean;
};

export function HeaderMobileNavSection({
  label,
  items,
  open,
  onToggle,
  locale,
  isLocationRoute,
}: Props) {
  return (
    <div className="py-2">
      <button
        className={`flex w-full items-center justify-between px-3 py-2 text-start text-white transition-colors ${
          isLocationRoute ? "hover:text-gold-200" : "hover:text-primary"
        }`}
        onClick={onToggle}
      >
        <span
          className={`font-semibold ${
            isLocationRoute ? "text-gold-200" : "text-primary"
          }`}
        >
          {label}
        </span>
        <Icon
          icon={open ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="h-5 w-5"
        />
      </button>
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ${open ? "max-h-[70vh]" : "max-h-0"}`}
      >
        <div className="flex flex-col gap-1 px-3 pb-2">
          {items.map((service) => (
            <Link
              key={service.href}
              href={localeNavHref(service.href, locale)}
              className={`rounded-md px-2 py-2 text-sm text-gray-300 hover:bg-neutral-800 ${
                isLocationRoute ? "hover:text-gold-200" : "hover:text-white"
              }`}
            >
              {service.title[locale]}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
